import { h } from './dom';
import { layers } from './layers';
import { sfx } from '../audio/sfx';

/**
 * The flight: a window seat over the wing, clouds sliding past, day turning to dusk,
 * captions and the cabin announcement. Same contract as playTrainRide.
 */
export function playFlight(lines: string[], announce: string, skipLabel: string, onDone: () => void, night = false): void {
  const wrap = document.getElementById('wrap');
  const LAYER = 'flight';
  const n = lines.length;
  const lineDur = n ? Math.max(2.2, 6.6 / n) : 0;
  const tAnnounce = 0.6 + n * lineDur + 0.2;
  const T = tAnnounce + 2.8;
  if (!wrap) return onDone();
  let done = false;
  let raf = 0;
  layers.open(LAYER);
  const cv = h('canvas', { class: 'trip-cv' });
  const cap = h('div', { class: 'trip-cap', 'aria-live': 'polite' });
  const sign = h('div', { class: 'trip-sign', 'aria-live': 'polite' }, h('span', { class: 'trip-sign-ico' }, '🔔'), h('span', { class: 'trip-sign-txt' }, announce));
  const finish = () => {
    if (done) return;
    done = true;
    root.classList.add('out');
    window.removeEventListener('keydown', onKey, true);
    window.setTimeout(() => {
      cancelAnimationFrame(raf);
      root.remove();
      layers.close(LAYER);
      onDone();
    }, 380);
  };
  const skip = h('button', { class: 'btn small trip-skip', onclick: finish }, skipLabel, ' ⏭');
  const root = h('div', { class: 'trip flight', role: 'dialog', 'aria-modal': 'true' }, cv, sign, cap, skip);
  wrap.append(root);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      finish();
    }
  };
  window.addEventListener('keydown', onKey, true);
  sfx('door');

  const ctx = cv.getContext('2d')!;
  const clouds = Array.from({ length: 14 }, (_, i) => ({ x: Math.random(), y: 0.35 + Math.random() * 0.5, s: 0.5 + Math.random() * 1.2, v: 0.03 + Math.random() * 0.05, i }));
  const t0 = performance.now();
  let line = -1;
  let announced = false;
  const frame = (now: number) => {
    if (done && !root.isConnected) return;
    const t = (now - t0) / 1000;
    const li = t < 0.6 ? -1 : Math.floor((t - 0.6) / lineDur);
    if (n && li !== line && li < n) {
      line = li;
      if (li >= 0) {
        const el = h('span', { class: 'trip-line' }, lines[li]);
        el.style.animationDuration = `${lineDur}s`;
        cap.replaceChildren(el);
      }
    }
    if (!announced && t >= tAnnounce) {
      announced = true;
      cap.replaceChildren();
      sign.classList.add('show');
      sfx('bell');
    }
    if (t >= T) finish();
    const r = root.getBoundingClientRect();
    const W = Math.max(2, Math.round(r.width)), H = Math.max(2, Math.round(r.height));
    if (cv.width !== W || cv.height !== H) {
      cv.width = W;
      cv.height = H;
    }
    const k = Math.min(1, t / T);
    // Sky: bright blue at take-off, deep dusk by landing (or the reverse on a night flight).
    const dusk = night ? 1 - k : k;
    const top = mix([70, 140, 230], [20, 24, 70], dusk), bot = mix([200, 230, 250], [240, 140, 90], dusk);
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, rgb(top));
    g.addColorStop(1, rgb(bot));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    // Sun / moon low on the horizon.
    ctx.fillStyle = dusk > 0.5 ? 'rgba(255,250,220,0.9)' : 'rgba(255,240,180,0.95)';
    ctx.beginPath();
    ctx.arc(W * 0.78, H * (0.28 + dusk * 0.12), 16 + dusk * 6, 0, Math.PI * 2);
    ctx.fill();
    // Clouds sliding by below the wing.
    for (const c of clouds) {
      const x = ((c.x - t * c.v * c.s) % 1 + 1) % 1;
      const cx = x * (W + 160) - 80, cy = c.y * H;
      ctx.fillStyle = `rgba(255,255,255,${0.75 - dusk * 0.3})`;
      for (let j = 0; j < 4; j++) {
        const rr = (14 + ((c.i * 7 + j * 5) % 9)) * c.s;
        ctx.beginPath();
        ctx.ellipse(cx + (j - 1.5) * rr * 0.9, cy + (j % 2) * rr * 0.3, rr, rr * 0.55, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    // The wing, with its winglet and the blinking light.
    ctx.fillStyle = '#d8dde3';
    ctx.beginPath();
    ctx.moveTo(-10, H * 0.62);
    ctx.lineTo(W * 0.72, H * 0.78);
    ctx.lineTo(W * 0.74, H * 0.7);
    ctx.lineTo(W * 0.3, H * 0.6);
    ctx.lineTo(-10, H * 0.56);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#b8bec6';
    ctx.fillRect(W * 0.1, H * 0.59, W * 0.5, 3);
    ctx.fillStyle = '#e2493b';
    ctx.fillRect(W * 0.72, H * 0.69, 4, H * 0.09);
    if (Math.floor(t * 2) % 2 === 0) {
      ctx.fillStyle = 'rgba(255,80,80,0.9)';
      ctx.beginPath();
      ctx.arc(W * 0.735, H * 0.69, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    // Window frame: a rounded cabin window around everything.
    ctx.fillStyle = '#ecebe6';
    ctx.beginPath();
    ctx.rect(0, 0, W, H);
    ctx.roundRect(W * 0.08, H * 0.08, W * 0.84, H * 0.76, 60);
    ctx.fill('evenodd');
    ctx.strokeStyle = '#c9c7bf';
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.roundRect(W * 0.08, H * 0.08, W * 0.84, H * 0.76, 60);
    ctx.stroke();
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
}

const mix = (a: number[], b: number[], k: number) => a.map((v, i) => v + (b[i] - v) * k);
const rgb = (c: number[]) => `rgb(${c[0] | 0},${c[1] | 0},${c[2] | 0})`;
